import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import type { PlanCommentThread } from "../api/types.js";
import { commentWhen } from "./plan-comments.js";
import type { PlanCommentsState, PlanCommentViewer } from "./use-plan-comments.js";

/**
 * The comments pinned to the plan, as text: every open thread with its number
 * (the number its pin carries), what it is on, who said it and when; the
 * selected one opened out with its replies, a reply box, and Resolve. Resolved
 * threads fold away under their own heading — history, not the to-do list.
 *
 * Also where a comment is written: dropping a pin with the Comment tool opens
 * the box here, focused, and nothing is posted until Post (or Ctrl+Enter).
 * Escape in the box, or Cancel, takes the pin back off the plan.
 *
 * The keyboard and screen-reader path to everything the pins do: a pin is
 * selected from its row as well as on the drawing.
 */

export interface CommentDraft {
  /** What the pin was dropped on, in words; null on open floor. */
  readonly about: string | null;
  readonly storeyLabel: string;
}

function Composer({
  label,
  placeholder,
  submitLabel,
  busy,
  autoFocus = false,
  initial = "",
  onSubmit,
  onCancel,
}: {
  readonly label: string;
  readonly placeholder: string;
  readonly submitLabel: string;
  readonly busy: boolean;
  readonly autoFocus?: boolean;
  readonly initial?: string;
  /** Resolves true when it was saved, so the box can be cleared; false keeps the text. */
  readonly onSubmit: (text: string) => Promise<boolean>;
  readonly onCancel?: () => void;
}): ReactNode {
  const [text, setText] = useState(initial);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    if (!text.trim() || busy) return;
    if (await onSubmit(text.trim())) setText("");
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void submit();
    } else if (event.key === "Escape" && onCancel) {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    }
  }

  return (
    <form className="floor-plan__comment-form" aria-label={label} onSubmit={(event) => void submit(event)}>
      <textarea
        ref={ref}
        className="floor-plan__comment-input"
        aria-label={label}
        placeholder={placeholder}
        value={text}
        maxLength={4000}
        rows={2}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={onKeyDown}
      />
      <div className="floor-plan__comment-actions">
        <button type="submit" className="floor-plan__tool floor-plan__tool--primary" disabled={busy || !text.trim()}>
          {busy ? "Posting…" : submitLabel}
        </button>
        {onCancel ? (
          <button type="button" className="floor-plan__link" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}

function ThreadItem({
  thread,
  about,
  storeyLabel,
  selected,
  viewer,
  state,
  onSelect,
}: {
  readonly thread: PlanCommentThread;
  readonly about: string | null;
  readonly storeyLabel: string | null;
  readonly selected: boolean;
  readonly viewer: PlanCommentViewer | undefined;
  readonly state: PlanCommentsState;
  readonly onSelect: (id: string | null) => void;
}): ReactNode {
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const canComment = viewer?.canComment === true;
  const mine = (authorId: string | undefined) => viewer !== undefined && authorId === viewer.userId;
  const mayDelete = (authorId: string | undefined) => mine(authorId) || viewer?.canModerate === true;
  const resolved = thread.status === "resolved";
  const where = [about, storeyLabel].filter((s): s is string => !!s).join(" · ");

  const body = (id: string, text: string, authorId: string | undefined, isThread: boolean) =>
    editing === id ? (
      <Composer
        label={isThread ? `Edit comment ${thread.number}` : "Edit reply"}
        placeholder=""
        submitLabel="Save"
        busy={state.busy}
        autoFocus
        initial={text}
        onSubmit={async (next) => {
          const saved = await state.update(id, { body: next });
          if (saved) setEditing(null);
          return saved !== null;
        }}
        onCancel={() => setEditing(null)}
      />
    ) : (
      <>
        <p className="floor-plan__comment-body">{text}</p>
        {selected ? (
          <p className="floor-plan__comment-tools">
            {mine(authorId) && canComment ? (
              <button type="button" className="floor-plan__link" onClick={() => setEditing(id)}>
                Edit
              </button>
            ) : null}
            {mayDelete(authorId) ? (
              confirmDelete === id ? (
                <span role="group" aria-label="Confirm delete">
                  {isThread && thread.replies.length > 0 ? "Delete it and its replies? " : "Delete it? "}
                  <button
                    type="button"
                    className="floor-plan__link floor-plan__link--danger"
                    disabled={state.busy}
                    onClick={() => {
                      setConfirmDelete(null);
                      void state.remove(id).then((done) => {
                        if (done && isThread) onSelect(null);
                      });
                    }}
                  >
                    Delete
                  </button>{" "}
                  <button type="button" className="floor-plan__link" onClick={() => setConfirmDelete(null)}>
                    Keep
                  </button>
                </span>
              ) : (
                <button type="button" className="floor-plan__link" onClick={() => setConfirmDelete(id)}>
                  Delete
                </button>
              )
            ) : null}
          </p>
        ) : null}
      </>
    );

  return (
    <li
      className={`floor-plan__comment${selected ? " is-selected" : ""}${resolved ? " is-resolved" : ""}`}
      data-comment-id={thread.id}
      data-testid="plan-comment"
    >
      <button
        type="button"
        className="floor-plan__comment-head"
        aria-pressed={selected}
        aria-label={`Comment ${thread.number}${where ? `, on ${where}` : ""}${resolved ? ", resolved" : ""}`}
        onClick={() => onSelect(selected ? null : thread.id)}
      >
        <span className="floor-plan__comment-no" aria-hidden="true">
          {thread.number}
        </span>
        <span className="floor-plan__comment-where">{where || "On the plan"}</span>
      </button>
      {body(thread.id, thread.body, thread.author?.id, true)}
      <p className="floor-plan__comment-meta">
        {thread.author?.name || "Someone"} · {commentWhen(thread.createdAt)}
        {thread.editedAt ? " · edited" : ""}
        {!selected && thread.replies.length > 0
          ? ` · ${thread.replies.length} ${thread.replies.length === 1 ? "reply" : "replies"}`
          : ""}
        {resolved && thread.resolvedBy ? ` · resolved by ${thread.resolvedBy.name}` : ""}
      </p>
      {selected ? (
        <>
          {thread.replies.length > 0 ? (
            <ul className="floor-plan__comment-replies" aria-label={`Replies to comment ${thread.number}`}>
              {thread.replies.map((reply) => (
                <li key={reply.id} className="floor-plan__comment-reply">
                  {body(reply.id, reply.body, reply.author?.id, false)}
                  <p className="floor-plan__comment-meta">
                    {reply.author?.name || "Someone"} · {commentWhen(reply.createdAt)}
                    {reply.editedAt ? " · edited" : ""}
                  </p>
                </li>
              ))}
            </ul>
          ) : null}
          {canComment && !resolved ? (
            <Composer
              label={`Reply to comment ${thread.number}`}
              placeholder="Reply…"
              submitLabel="Reply"
              busy={state.busy}
              onSubmit={async (text) => (await state.reply(thread.id, text)) !== null}
            />
          ) : null}
          {canComment ? (
            <p className="floor-plan__comment-actions">
              <button
                type="button"
                className="floor-plan__tool"
                disabled={state.busy}
                onClick={() => void state.update(thread.id, { status: resolved ? "open" : "resolved" })}
              >
                {resolved ? "Reopen" : "Resolve"}
              </button>
            </p>
          ) : null}
        </>
      ) : null}
    </li>
  );
}

export function PlanCommentsPanel({
  placement,
  state,
  viewer,
  commenting,
  draft,
  onPostDraft,
  onCancelDraft,
  selectedId,
  onSelect,
  showResolved,
  onToggleResolved,
  aboutOf,
  storeyLabelOf,
}: {
  readonly placement: "side" | "below";
  readonly state: PlanCommentsState;
  readonly viewer: PlanCommentViewer | undefined;
  /** The Comment tool is in hand. */
  readonly commenting: boolean;
  readonly draft: CommentDraft | null;
  readonly onPostDraft: (body: string) => Promise<boolean>;
  readonly onCancelDraft: () => void;
  readonly selectedId: string | null;
  readonly onSelect: (id: string | null) => void;
  readonly showResolved: boolean;
  readonly onToggleResolved: () => void;
  readonly aboutOf: (thread: PlanCommentThread) => string | null;
  /** The storey's name, or null when the plan has one storey (nothing to say). */
  readonly storeyLabelOf: (storey: number) => string | null;
}): ReactNode {
  if (state.status === "unavailable") return null;
  const open = state.threads.filter((t) => t.status === "open");
  const resolved = state.threads.filter((t) => t.status === "resolved");
  const item = (thread: PlanCommentThread) => (
    <ThreadItem
      key={thread.id}
      thread={thread}
      about={aboutOf(thread)}
      storeyLabel={storeyLabelOf(thread.pin.storey)}
      selected={thread.id === selectedId}
      viewer={viewer}
      state={state}
      onSelect={onSelect}
    />
  );

  return (
    <aside
      className={`floor-plan__panel floor-plan__panel--${placement} floor-plan__comments`}
      aria-label="Comments on the plan"
      style={{ minWidth: 0 }}
    >
      <p className="floor-plan__panel-title">
        Comments <span className="muted">· {open.length} open</span>
      </p>

      {state.status === "unsupported" ? (
        <p className="floor-plan__panel-note">This server does not keep comments on the plan.</p>
      ) : state.status === "failed" ? (
        <p className="floor-plan__panel-error" role="alert">
          The comments could not be loaded. {state.loadError}{" "}
          <button type="button" className="floor-plan__link" onClick={state.reload}>
            Try again
          </button>
        </p>
      ) : null}

      {commenting ? (
        <p className="floor-plan__panel-hint" role="status">
          Click where the comment applies. On a wall, window, door, radiator or room it stays with that element when
          it moves. Enter drops it at the pointer; Esc cancels.
        </p>
      ) : draft ? null : open.length === 0 && state.status === "ready" && viewer?.canComment ? (
        <p className="floor-plan__panel-hint">
          Press Comment and click the plan to pin a note where it applies — &ldquo;check this radiator
          position&rdquo; — for the surveyor, designer and installer.
        </p>
      ) : null}

      {draft ? (
        <div className="floor-plan__comment-draft" data-testid="plan-comment-draft">
          <p className="floor-plan__comment-meta">
            New comment{draft.about ? ` on ${draft.about}` : ""} · {draft.storeyLabel}
          </p>
          <Composer
            label="New comment"
            placeholder="What should they know or check here?"
            submitLabel="Post"
            busy={state.busy}
            autoFocus
            onSubmit={onPostDraft}
            onCancel={onCancelDraft}
          />
        </div>
      ) : null}

      {state.error ? (
        <p className="floor-plan__panel-error" role="alert" data-testid="plan-comment-error">
          {state.error}{" "}
          <button type="button" className="floor-plan__link" onClick={state.dismissError}>
            Dismiss
          </button>
        </p>
      ) : null}

      {state.status === "loading" ? (
        <p className="floor-plan__panel-empty">Loading comments…</p>
      ) : open.length === 0 ? (
        <p className="floor-plan__panel-empty">No open comments.</p>
      ) : (
        <ul className="floor-plan__panel-list floor-plan__comment-list" aria-label="Open comments">
          {open.map(item)}
        </ul>
      )}

      {resolved.length > 0 ? (
        <>
          <button
            type="button"
            className="floor-plan__link floor-plan__comment-toggle"
            aria-expanded={showResolved}
            onClick={onToggleResolved}
          >
            {showResolved ? "Hide" : "Show"} resolved ({resolved.length})
          </button>
          {showResolved ? (
            <ul className="floor-plan__panel-list floor-plan__comment-list" aria-label="Resolved comments">
              {resolved.map(item)}
            </ul>
          ) : null}
        </>
      ) : null}
    </aside>
  );
}
